import type { ReactElement } from 'react';
import { LOCALES, type Locale } from '@gr/core/platform/user-preferences/locales';
import { COLOR_MODES, THEMES, type ColorMode, type ThemeName } from '@gr/core/platform/user-preferences/themes';
import { useT } from '../../../../app/di';
import { ChoiceGroup, ChoiceTile, SegmentedControl } from '../../../../ui-kit/Choice/Choice';
import { SelectField } from '../../../../ui-kit/Field/Field';
import styles from './PreferencesPanel.module.scss';

export interface PreferencesPanelProps {
  locale: Locale;
  theme: ThemeName;
  mode: ColorMode;
  /** Узкая колонка (страница входа): темы в две колонки. */
  compact?: boolean;
  onLocaleChange(locale: Locale): void;
  onThemeChange(theme: ThemeName): void;
  onModeChange(mode: ColorMode): void;
}

/** PREF-UC-1, PREF-UC-2: язык, тема оформления и режим. */
export function PreferencesPanel({ locale, theme, mode, compact = false, onLocaleChange, onThemeChange, onModeChange }: PreferencesPanelProps): ReactElement {
  const { t } = useT();
  return (
    <div className={styles.panel}>
      <SelectField
        label={t('preferences.language')}
        value={locale}
        options={LOCALES.map((l) => ({ value: l.id, label: l.nativeName }))}
        onChange={(event) => onLocaleChange(event.target.value as Locale)}
      />
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{t('preferences.theme')}</legend>
        <ChoiceGroup label={t('preferences.theme')} columns={compact ? 'two' : 'auto'}>
          {THEMES.map((item) => (
            <ChoiceTile key={item.id} checked={theme === item.id} title={t(`preferences.themeHints.${item.id}`)} onSelect={() => onThemeChange(item.id)}>
              <span className={styles.swatches} aria-hidden="true">
                {item.swatches.map((color) => (
                  // Образцы показывают цвета самих тем, поэтому задаются данными, а не токенами.
                  <span key={color} className={styles.swatch} style={{ background: color }} />
                ))}
              </span>
              <span className={styles.themeName}>
                {t(`preferences.themes.${item.id}`)}
                {item.pastel ? <span className={styles.pastel}> · {t('preferences.pastel')}</span> : null}
              </span>
            </ChoiceTile>
          ))}
        </ChoiceGroup>
      </fieldset>
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{t('preferences.mode')}</legend>
        <SegmentedControl label={t('preferences.mode')} value={mode} options={COLOR_MODES.map((value) => ({ value, label: t(`preferences.modes.${value}`) }))} onChange={onModeChange} />
      </fieldset>
    </div>
  );
}
